import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Palette, Users, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Overline } from "@/components/Cards";
import { RANGER_AVATAR, HERO_MURAL } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const VALUES = [
  { icon: Palette, title: "Champion the Creative", text: "We spotlight the artists, makers, and independent businesses that give RiNo its unmistakable edge." },
  { icon: Users, title: "Serve the District", text: "As a Destination Marketing Organization, our job is to bring visitors, dollars, and attention to RiNo." },
  { icon: TrendingUp, title: "Grow Together", text: "Every member's success is the district's success. We build tools, promotions, and audiences for all." },
  { icon: Sparkles, title: "Powered by The Ranger", text: "Our AI editor produces fresh, accurate, SEO-strong content daily so RiNo always leads the conversation." },
];

export default function About() {
  return (
    <div>
      <Seo title="About Visit RiNo" description="Visit RiNo is the Destination Marketing Organization for Denver's River North Art District — championing local business, art, and culture." image={HERO_MURAL} />
      <PageHeader overline="Who We Are" title="The Destination Marketing Organization for RiNo" subtitle="Visit RiNo exists to put Denver's River North Art District on the map — and keep it there." image={HERO_MURAL} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <Overline>Our Mission</Overline>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white mt-3">Making RiNo the most discoverable art district in America</h2>
          <p className="text-neutral-400 mt-5 leading-relaxed text-lg">
            RiNo grew from an industrial corridor into one of Denver's most electric neighborhoods — a canvas of murals, breweries, galleries, studios, and restaurants. We tell that story to the world.
          </p>
          <p className="text-neutral-400 mt-4 leading-relaxed">
            We build the most complete business directory in the district, produce best-in-class local content, run district-wide promotions, and give every member the tools to thrive online and off.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {VALUES.map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="border border-white/10 bg-[#141414] p-6 hover:border-[#FF0055] transition-colors">
              <v.icon className="text-[#00E5FF] mb-3" size={22} />
              <h3 className="font-display font-bold text-white">{v.title}</h3>
              <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{v.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="border border-white/10 bg-[#141414] grain relative p-8 sm:p-12 flex flex-col md:flex-row gap-8 items-center">
          <img src={RANGER_AVATAR} alt="The RiNo Ranger" className="relative z-10 w-40 h-40 object-cover border border-[#FF0055]" />
          <div className="relative z-10">
            <Overline>Always On</Overline>
            <h2 className="font-display font-extrabold text-3xl text-white mt-2">Meet The RiNo Ranger</h2>
            <p className="text-neutral-400 mt-3 leading-relaxed max-w-2xl">
              The RiNo Ranger is our AI agent and the beating heart of Visit RiNo. It learns everything about the district and its businesses, writes weekly guides and news, keeps listings accurate, and nurtures relationships with members and visitors alike — every single day.
            </p>
            <Link to="/guides"><Button className="mt-6 bg-[#00E5FF] text-black hover:bg-[#33ebff] rounded-none font-semibold">See the Ranger's work <ArrowRight size={16} /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
